import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';

const STEPS = 16;
const DEFAULT_BPM = 142;
const STORAGE_KEY = 'makingitmix.beatmaker.v1';

const trackPresets = [
  { id: 'kick', name: 'Kick', short: 'KCK', color: '#f05365', gain: 0.95, pattern: [0, 4, 8, 12] },
  { id: 'snare', name: 'Snare', short: 'SNR', color: '#ffb000', gain: 0.72, pattern: [4, 12] },
  { id: 'hat', name: 'Hi-Hat', short: 'HAT', color: '#45d483', gain: 0.38, pattern: [2, 6, 10, 14] },
  { id: 'openhat', name: 'Open Hat', short: 'OPN', color: '#38bdf8', gain: 0.34, pattern: [7, 15] },
  { id: 'bass', name: '808 Bass', short: '808', color: '#b778ff', gain: 0.82, pattern: [0, 3, 8, 11] },
  { id: 'melody', name: 'Lead', short: 'LED', color: '#f472b6', gain: 0.45, pattern: [0, 2, 5, 7, 10, 13] },
];

const scales = {
  minor: [0, 3, 5, 7, 10, 12, 15, 19],
  drill: [0, 2, 3, 6, 7, 10, 12, 14],
  rnb: [0, 2, 4, 7, 9, 11, 14, 16],
};

const keys = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];
const noteNames = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];
const keyOffsets = keys.reduce((map, key, index) => ({ ...map, [key]: index }), {});

const createInitialTracks = () =>
  trackPresets.map((track) => ({
    ...track,
    muted: false,
    solo: false,
    volume: Math.round(track.gain * 100),
    steps: Array.from({ length: STEPS }, (_, index) => track.pattern.includes(index)),
  }));

const noteToFrequency = (note) => {
  const match = /^([A-G]#?|Bb|Eb|Ab)(\d)$/.exec(note);
  if (!match) return 261.63;
  const noteName = match[1];
  const octave = Number(match[2]);
  const semitoneMap = { C: 0, 'C#': 1, D: 2, Eb: 3, E: 4, F: 5, 'F#': 6, G: 7, Ab: 8, A: 9, Bb: 10, B: 11 };
  const midi = 12 * (octave + 1) + semitoneMap[noteName];
  return 440 * 2 ** ((midi - 69) / 12);
};

const noteForStep = (scaleName, rootKey, stepIndex) => {
  const intervals = scales[scaleName] || scales.minor;
  const interval = intervals[stepIndex % intervals.length];
  const rootOffset = keyOffsets[rootKey] ?? 0;
  const midi = 48 + rootOffset + interval;
  const noteName = noteNames[midi % 12];
  const octave = Math.floor(midi / 12) - 1;
  return `${noteName}${octave}`;
};

const makeNoiseBuffer = (audioContext, seconds = 1) => {
  const buffer = audioContext.createBuffer(1, audioContext.sampleRate * seconds, audioContext.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
};

const connectVoice = (audioContext, destination, gain, pan = 0) => {
  const voiceGain = audioContext.createGain();
  const panner = audioContext.createStereoPanner ? audioContext.createStereoPanner() : null;
  voiceGain.gain.value = gain;
  if (panner) {
    panner.pan.value = pan;
    voiceGain.connect(panner);
    panner.connect(destination);
  } else {
    voiceGain.connect(destination);
  }
  return voiceGain;
};

const playKick = (audioContext, destination, time, gain) => {
  const osc = audioContext.createOscillator();
  const amp = connectVoice(audioContext, destination, gain);
  osc.type = 'sine';
  osc.frequency.setValueAtTime(140, time);
  osc.frequency.exponentialRampToValueAtTime(42, time + 0.28);
  amp.gain.setValueAtTime(gain, time);
  amp.gain.exponentialRampToValueAtTime(0.001, time + 0.34);
  osc.connect(amp);
  osc.start(time);
  osc.stop(time + 0.36);
};

const playSnare = (audioContext, destination, time, gain) => {
  const noise = audioContext.createBufferSource();
  noise.buffer = makeNoiseBuffer(audioContext, 0.22);
  const noiseGain = connectVoice(audioContext, destination, gain * 0.75, -0.08);
  const filter = audioContext.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 1800;
  noiseGain.gain.setValueAtTime(gain * 0.75, time);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, time + 0.18);
  noise.connect(filter);
  filter.connect(noiseGain);
  noise.start(time);
  noise.stop(time + 0.2);

  const body = audioContext.createOscillator();
  const bodyGain = connectVoice(audioContext, destination, gain * 0.24);
  body.type = 'triangle';
  body.frequency.value = 190;
  bodyGain.gain.setValueAtTime(gain * 0.24, time);
  bodyGain.gain.exponentialRampToValueAtTime(0.001, time + 0.11);
  body.connect(bodyGain);
  body.start(time);
  body.stop(time + 0.13);
};

const playHat = (audioContext, destination, time, gain, open = false) => {
  const noise = audioContext.createBufferSource();
  noise.buffer = makeNoiseBuffer(audioContext, open ? 0.5 : 0.12);
  const filter = audioContext.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = open ? 5200 : 7200;
  const amp = connectVoice(audioContext, destination, gain, open ? 0.18 : 0.08);
  amp.gain.setValueAtTime(gain, time);
  amp.gain.exponentialRampToValueAtTime(0.001, time + (open ? 0.42 : 0.08));
  noise.connect(filter);
  filter.connect(amp);
  noise.start(time);
  noise.stop(time + (open ? 0.45 : 0.1));
};

const playBass = (audioContext, destination, time, gain, note) => {
  const osc = audioContext.createOscillator();
  const sub = audioContext.createOscillator();
  const amp = connectVoice(audioContext, destination, gain);
  const freq = noteToFrequency(note) / 2;
  osc.type = 'sawtooth';
  sub.type = 'sine';
  osc.frequency.setValueAtTime(freq, time);
  sub.frequency.setValueAtTime(freq / 2, time);
  amp.gain.setValueAtTime(0.0001, time);
  amp.gain.exponentialRampToValueAtTime(gain, time + 0.02);
  amp.gain.exponentialRampToValueAtTime(0.001, time + 0.42);
  osc.connect(amp);
  sub.connect(amp);
  osc.start(time);
  sub.start(time);
  osc.stop(time + 0.44);
  sub.stop(time + 0.44);
};

const playLead = (audioContext, destination, time, gain, note) => {
  const osc = audioContext.createOscillator();
  const amp = connectVoice(audioContext, destination, gain, -0.15);
  const filter = audioContext.createBiquadFilter();
  osc.type = 'square';
  osc.frequency.value = noteToFrequency(note);
  filter.type = 'lowpass';
  filter.frequency.setValueAtTime(1400, time);
  filter.frequency.exponentialRampToValueAtTime(420, time + 0.25);
  amp.gain.setValueAtTime(0.0001, time);
  amp.gain.exponentialRampToValueAtTime(gain, time + 0.01);
  amp.gain.exponentialRampToValueAtTime(0.001, time + 0.26);
  osc.connect(filter);
  filter.connect(amp);
  osc.start(time);
  osc.stop(time + 0.28);
};

const playTrackSound = (audioContext, destination, trackId, time, gain, note) => {
  if (trackId === 'kick') playKick(audioContext, destination, time, gain);
  if (trackId === 'snare') playSnare(audioContext, destination, time, gain);
  if (trackId === 'hat') playHat(audioContext, destination, time, gain);
  if (trackId === 'openhat') playHat(audioContext, destination, time, gain, true);
  if (trackId === 'bass') playBass(audioContext, destination, time, gain, note);
  if (trackId === 'melody') playLead(audioContext, destination, time, gain, note);
};

const encodeWav = (audioBuffer) => {
  const channelData = audioBuffer.getChannelData(0);
  const sampleRate = audioBuffer.sampleRate;
  const bytesPerSample = 2;
  const buffer = new ArrayBuffer(44 + channelData.length * bytesPerSample);
  const view = new DataView(buffer);
  const writeString = (offset, value) => {
    for (let i = 0; i < value.length; i += 1) view.setUint8(offset + i, value.charCodeAt(i));
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + channelData.length * bytesPerSample, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true);
  view.setUint16(32, bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, channelData.length * bytesPerSample, true);

  let offset = 44;
  for (let i = 0; i < channelData.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, channelData[i]));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
    offset += 2;
  }

  return new Blob([buffer], { type: 'audio/wav' });
};

const MixerNewPage = () => {
  const location = useLocation();
  const [tracks, setTracks] = useState(createInitialTracks);
  const [bpm, setBpm] = useState(DEFAULT_BPM);
  const [swing, setSwing] = useState(12);
  const [master, setMaster] = useState(88);
  const [scale, setScale] = useState('minor');
  const [rootKey, setRootKey] = useState('C');
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [patternName, setPatternName] = useState('New Beat');
  const audioContextRef = useRef(null);
  const intervalRef = useRef(null);
  const stepRef = useRef(0);
  const tracksRef = useRef(tracks);
  const settingsRef = useRef({ bpm, swing, master, scale, rootKey });

  const activeSteps = useMemo(
    () => tracks.reduce((total, track) => total + track.steps.filter(Boolean).length, 0),
    [tracks]
  );

  const hasSolo = tracks.some((track) => track.solo);
  useEffect(() => {
    tracksRef.current = tracks;
    settingsRef.current = { bpm, swing, master, scale, rootKey };
  }, [bpm, master, rootKey, scale, swing, tracks]);

  useEffect(() => {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    const statePatternName = location?.state?.patternName?.trim();
    if (!saved) return;
    try {
      const data = JSON.parse(saved);
      if (Array.isArray(data.tracks)) setTracks(data.tracks);
      if (data.bpm) setBpm(data.bpm);
      if (data.swing) setSwing(data.swing);
      if (data.master) setMaster(data.master);
      if (data.scale) setScale(data.scale);
      if (data.rootKey) setRootKey(data.rootKey);
      if (statePatternName) setPatternName(statePatternName);
      else if (data.patternName) setPatternName(data.patternName);
    } catch (error) {
      console.warn('Unable to load saved beat pattern', error);
    }
  }, [location?.state?.patternName]);

  useEffect(() => {
    const statePatternName = location?.state?.patternName?.trim();
    if (statePatternName) setPatternName(statePatternName);
  }, [location?.state?.patternName]);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AudioContextClass();
    }
    return audioContextRef.current;
  }, []);

  const playStep = useCallback((stepIndex, when = null, contextOverride = null, destinationOverride = null) => {
    const audioContext = contextOverride || getAudioContext();
    const destination = destinationOverride || audioContext.destination;
    const now = when ?? audioContext.currentTime;
    const { master: masterLevel, scale: selectedScale, rootKey: selectedRootKey } = settingsRef.current;
    const soloEnabled = tracksRef.current.some((track) => track.solo);

    tracksRef.current.forEach((track) => {
      if (!track.steps[stepIndex] || track.muted || (soloEnabled && !track.solo)) return;
      const note = noteForStep(selectedScale, selectedRootKey, stepIndex);
      const gain = (track.volume / 100) * (masterLevel / 100);
      playTrackSound(audioContext, destination, track.id, now, gain, note);
    });
  }, [getAudioContext]);

  const stop = useCallback(() => {
    window.clearInterval(intervalRef.current);
    intervalRef.current = null;
    stepRef.current = 0;
    setIsPlaying(false);
    setCurrentStep(-1);
  }, []);

  const start = useCallback(async () => {
    const audioContext = getAudioContext();
    if (audioContext.state === 'suspended') await audioContext.resume();
    window.clearInterval(intervalRef.current);
    setIsPlaying(true);

    const scheduleNext = () => {
      const stepIndex = stepRef.current % STEPS;
      setCurrentStep(stepIndex);
      playStep(stepIndex);
      stepRef.current = (stepRef.current + 1) % STEPS;
      const swingDelay = stepIndex % 2 === 1 ? swing / 100 : 0;
      const nextMs = (60 / bpm / 4) * 1000 * (1 + swingDelay);
      window.clearInterval(intervalRef.current);
      intervalRef.current = window.setInterval(scheduleNext, nextMs);
    };

    scheduleNext();
  }, [bpm, getAudioContext, playStep, swing]);

  useEffect(() => () => stop(), [stop]);

  const togglePlayback = () => {
    if (isPlaying) stop();
    else start();
  };

  const triggerPad = async (track) => {
    const audioContext = getAudioContext();
    if (audioContext.state === 'suspended') await audioContext.resume();
    const note = noteForStep(scale, rootKey, Math.floor(Math.random() * STEPS));
    playTrackSound(audioContext, audioContext.destination, track.id, audioContext.currentTime, (track.volume / 100) * (master / 100), note);
  };

  const updateTrack = (trackId, updater) => {
    setTracks((current) => current.map((track) => (track.id === trackId ? updater(track) : track)));
  };

  const toggleStep = (trackId, stepIndex) => {
    updateTrack(trackId, (track) => ({
      ...track,
      steps: track.steps.map((active, index) => (index === stepIndex ? !active : active)),
    }));
  };

  const clearPattern = () => {
    setTracks((current) => current.map((track) => ({ ...track, steps: Array(STEPS).fill(false) })));
  };

  const loadTemplate = (template) => {
    const maps = {
      trap: {
        kick: [0, 6, 10, 14],
        snare: [4, 12],
        hat: [1, 3, 5, 7, 9, 11, 13, 15],
        openhat: [7, 15],
        bass: [0, 3, 6, 10, 14],
        melody: [0, 2, 5, 9, 13],
      },
      drill: {
        kick: [0, 5, 9, 11, 14],
        snare: [4, 12],
        hat: [0, 2, 3, 6, 8, 10, 11, 14],
        openhat: [6, 15],
        bass: [0, 5, 8, 11, 14],
        melody: [0, 3, 6, 10, 12, 15],
      },
      rnb: {
        kick: [0, 7, 10],
        snare: [4, 12],
        hat: [2, 6, 10, 14],
        openhat: [15],
        bass: [0, 6, 9, 13],
        melody: [0, 2, 4, 7, 9, 11, 14],
      },
    };
    const map = maps[template];
    setPatternName(`${template.toUpperCase()} Starter`);
    setTracks((current) =>
      current.map((track) => ({
        ...track,
        steps: Array.from({ length: STEPS }, (_, index) => map[track.id]?.includes(index) || false),
      }))
    );
  };

  const randomizePattern = () => {
    setTracks((current) =>
      current.map((track) => ({
        ...track,
        steps: Array.from({ length: STEPS }, (_, index) => {
          if (track.id === 'snare') return index === 4 || index === 12;
          const density = track.id === 'hat' ? 0.48 : track.id === 'melody' ? 0.32 : 0.25;
          return Math.random() < density;
        }),
      }))
    );
  };

  const savePattern = () => {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ tracks, bpm, swing, master, scale, rootKey, patternName, savedAt: new Date().toISOString() })
    );
  };

  const exportWav = async () => {
    setIsExporting(true);
    try {
      const sampleRate = 44100;
      const bars = 4;
      const seconds = (60 / bpm) * 4 * bars;
      const offlineContext = new OfflineAudioContext(1, Math.ceil(seconds * sampleRate), sampleRate);
      const masterGain = offlineContext.createGain();
      masterGain.gain.value = 0.86;
      masterGain.connect(offlineContext.destination);
      const stepDuration = 60 / bpm / 4;

      for (let bar = 0; bar < bars; bar += 1) {
        for (let step = 0; step < STEPS; step += 1) {
          const swingOffset = step % 2 === 1 ? stepDuration * (swing / 100) : 0;
          playStep(step, bar * STEPS * stepDuration + step * stepDuration + swingOffset, offlineContext, masterGain);
        }
      }

      const rendered = await offlineContext.startRendering();
      const url = URL.createObjectURL(encodeWav(rendered));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${patternName.trim().replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'makingitmix-beat'}.wav`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>MakingItMix Beat Maker - Browser Sequencer</title>
      </Helmet>
      <main className="min-h-screen bg-[#070806] text-[#f7f4ea]">
        <div className="border-b border-[#26271f] bg-[#10110d]/95 px-4 py-3">
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
            <Link to="/" className="flex min-w-0 items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-[#ffb000] text-[#120d03] shadow-[0_0_28px_rgba(255,176,0,0.28)]">
                <Icon name="Drum" size={20} />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-lg font-semibold leading-tight">MakingItMix Beat Maker</span>
                <span className="block text-[10px] uppercase tracking-[0.28em] text-[#9c9a8d]">Sequencer</span>
              </span>
            </Link>

            <div className="hidden items-center gap-2 rounded border border-[#2b2c22] bg-[#090a07] px-3 py-2 text-xs text-[#b7b39e] md:flex">
              <Icon name="Activity" size={15} className="text-[#45d483]" />
              {activeSteps} active steps
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={togglePlayback}
                className={`flex h-10 items-center gap-2 rounded px-4 text-sm font-semibold ${
                  isPlaying ? 'bg-[#f05365] text-white' : 'bg-[#45d483] text-[#071008]'
                }`}
              >
                <Icon name={isPlaying ? 'Square' : 'Play'} size={16} />
                <span className="hidden sm:inline">{isPlaying ? 'Stop' : 'Play'}</span>
              </button>
              <button
                type="button"
                onClick={exportWav}
                disabled={isExporting}
                className="flex h-10 items-center gap-2 rounded border border-[#3b3d31] px-3 text-sm font-semibold text-[#f7f4ea] hover:border-[#ffb000] disabled:opacity-50"
              >
                <Icon name="Download" size={16} />
                <span className="hidden sm:inline">{isExporting ? 'Exporting' : 'WAV'}</span>
              </button>
            </div>
          </div>
        </div>

        <section className="mx-auto grid max-w-7xl gap-5 px-4 py-5 lg:grid-cols-[280px_1fr]">
          <aside className="space-y-4">
            <section className="rounded border border-[#25271e] bg-[#10110d] p-4">
              <label className="mb-3 block">
                <span className="mb-2 block text-[10px] uppercase tracking-[0.22em] text-[#9c9a8d]">Pattern name</span>
                <input
                  value={patternName}
                  onChange={(event) => setPatternName(event.target.value)}
                  className="w-full rounded border border-[#303226] bg-[#070806] px-3 py-2 text-sm outline-none focus:border-[#ffb000]"
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label>
                  <span className="mb-2 block text-[10px] uppercase tracking-[0.22em] text-[#9c9a8d]">BPM</span>
                  <input
                    type="number"
                    min="60"
                    max="220"
                    value={bpm}
                    onChange={(event) => setBpm(Number(event.target.value))}
                    className="w-full rounded border border-[#303226] bg-[#070806] px-3 py-2 text-sm outline-none focus:border-[#ffb000]"
                  />
                </label>
                <label>
                  <span className="mb-2 block text-[10px] uppercase tracking-[0.22em] text-[#9c9a8d]">Swing</span>
                  <input
                    type="number"
                    min="0"
                    max="40"
                    value={swing}
                    onChange={(event) => setSwing(Number(event.target.value))}
                    className="w-full rounded border border-[#303226] bg-[#070806] px-3 py-2 text-sm outline-none focus:border-[#ffb000]"
                  />
                </label>
              </div>

              <label className="mt-4 block">
                <span className="mb-2 block text-[10px] uppercase tracking-[0.22em] text-[#9c9a8d]">Master</span>
                <input type="range" min="0" max="100" value={master} onChange={(event) => setMaster(Number(event.target.value))} className="w-full accent-[#ffb000]" />
              </label>
            </section>

            <section className="rounded border border-[#25271e] bg-[#10110d] p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <Icon name="Piano" size={16} className="text-[#f472b6]" />
                Key and Scale
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select value={rootKey} onChange={(event) => setRootKey(event.target.value)} className="rounded border border-[#303226] bg-[#070806] px-3 py-2 text-sm outline-none focus:border-[#ffb000]">
                  {keys.map((key) => <option key={key}>{key}</option>)}
                </select>
                <select value={scale} onChange={(event) => setScale(event.target.value)} className="rounded border border-[#303226] bg-[#070806] px-3 py-2 text-sm outline-none focus:border-[#ffb000]">
                  <option value="minor">Minor</option>
                  <option value="drill">Drill Dark</option>
                  <option value="rnb">R&B Bright</option>
                </select>
              </div>
            </section>

            <section className="rounded border border-[#25271e] bg-[#10110d] p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <Icon name="MousePointerClick" size={16} className="text-[#45d483]" />
                Live Pads
              </div>
              <div className="grid grid-cols-2 gap-2">
                {tracks.map((track) => (
                  <button
                    key={track.id}
                    type="button"
                    onClick={() => triggerPad(track)}
                    className="h-14 rounded border border-[#303226] bg-[#090a07] text-xs font-semibold transition hover:-translate-y-0.5 hover:border-current"
                    style={{ color: track.color }}
                  >
                    {track.short}
                  </button>
                ))}
              </div>
            </section>

            <section className="grid grid-cols-2 gap-2">
              {['trap', 'drill', 'rnb'].map((template) => (
                <button key={template} type="button" onClick={() => loadTemplate(template)} className="rounded border border-[#303226] bg-[#10110d] px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] hover:border-[#ffb000]">
                  {template}
                </button>
              ))}
              <button type="button" onClick={randomizePattern} className="rounded border border-[#303226] bg-[#10110d] px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] hover:border-[#ffb000]">Random</button>
              <button type="button" onClick={clearPattern} className="rounded border border-[#303226] bg-[#10110d] px-3 py-2 text-xs font-semibold uppercase tracking-[0.12em] hover:border-[#f05365]">Clear</button>
              <button type="button" onClick={savePattern} className="rounded bg-[#ffb000] px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] text-[#120d03]">Save</button>
            </section>
          </aside>

          <section className="min-w-0 rounded border border-[#25271e] bg-[#10110d] p-3 md:p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">Build the Beat</h1>
                <p className="mt-1 text-sm text-[#a9a58f]">Click steps, hit play, then export a four-bar WAV loop.</p>
              </div>
              <div className="grid grid-cols-4 gap-1 text-center text-[10px] uppercase tracking-[0.18em] text-[#8b8978]">
                {[1, 2, 3, 4].map((bar) => <span key={bar}>Bar {bar}</span>)}
              </div>
            </div>

            <div className="overflow-x-auto">
              <div className="min-w-[760px] space-y-2 xl:min-w-0">
                <div className="grid grid-cols-[112px_repeat(16,minmax(26px,1fr))_108px] gap-1.5 text-center text-[10px] uppercase tracking-[0.16em] text-[#777563] xl:grid-cols-[124px_repeat(16,minmax(30px,1fr))_124px]">
                  <span />
                  {Array.from({ length: STEPS }, (_, index) => (
                    <span key={index} className={index % 4 === 0 ? 'text-[#ffb000]' : ''}>{index + 1}</span>
                  ))}
                  <span />
                </div>

                {tracks.map((track) => (
                  <div key={track.id} className="grid grid-cols-[112px_repeat(16,minmax(26px,1fr))_108px] items-center gap-1.5 xl:grid-cols-[124px_repeat(16,minmax(30px,1fr))_124px]">
                    <button
                      type="button"
                      onClick={() => triggerPad(track)}
                      className="flex h-10 items-center justify-between rounded border border-[#303226] bg-[#090a07] px-2 text-left text-xs font-semibold xl:h-11 xl:px-3 xl:text-sm"
                      style={{ color: track.color }}
                    >
                      <span>{track.name}</span>
                      <Icon name="Volume2" size={14} />
                    </button>
                    {track.steps.map((active, stepIndex) => (
                      <button
                        key={`${track.id}-${stepIndex}`}
                        type="button"
                        onClick={() => toggleStep(track.id, stepIndex)}
                        className={`h-10 rounded border transition xl:h-11 ${
                          currentStep === stepIndex ? 'scale-[1.03] border-[#f7f4ea]' : 'border-[#2d2f24]'
                        } ${active ? 'shadow-[0_0_18px_rgba(255,176,0,0.12)]' : 'bg-[#080906] hover:border-[#65624d]'}`}
                        style={{ backgroundColor: active ? track.color : undefined }}
                        aria-label={`${track.name} step ${stepIndex + 1}`}
                      />
                    ))}
                    <div className="flex h-10 items-center gap-1.5 rounded border border-[#303226] bg-[#090a07] px-1.5 xl:h-11 xl:gap-2 xl:px-2">
                      <button
                        type="button"
                        onClick={() => updateTrack(track.id, (item) => ({ ...item, muted: !item.muted }))}
                        className={`h-7 w-8 rounded text-[10px] font-bold ${track.muted ? 'bg-[#f05365] text-white' : 'bg-[#202219] text-[#b9b6a5]'}`}
                      >
                        M
                      </button>
                      <button
                        type="button"
                        onClick={() => updateTrack(track.id, (item) => ({ ...item, solo: !item.solo }))}
                        className={`h-7 w-8 rounded text-[10px] font-bold ${track.solo ? 'bg-[#45d483] text-[#061008]' : 'bg-[#202219] text-[#b9b6a5]'}`}
                      >
                        S
                      </button>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={track.volume}
                        onChange={(event) => updateTrack(track.id, (item) => ({ ...item, volume: Number(event.target.value) }))}
                        className="min-w-0 flex-1 accent-[#ffb000]"
                        aria-label={`${track.name} volume`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-5 grid gap-3 md:grid-cols-3">
              <div className="rounded border border-[#25271e] bg-[#090a07] p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#8b8978]">Playback</div>
                <div className="mt-2 text-2xl font-semibold">{isPlaying ? `Step ${currentStep + 1}` : 'Stopped'}</div>
              </div>
              <div className="rounded border border-[#25271e] bg-[#090a07] p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#8b8978]">Groove</div>
                <div className="mt-2 text-2xl font-semibold">{bpm} BPM / {swing}%</div>
              </div>
              <div className="rounded border border-[#25271e] bg-[#090a07] p-4">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#8b8978]">Mix state</div>
                <div className="mt-2 text-2xl font-semibold">{hasSolo ? 'Solo active' : `${master}% master`}</div>
              </div>
            </div>
          </section>
        </section>
      </main>
    </>
  );
};

export default MixerNewPage;
